import { Play } from "lucide-react";
import { useState } from "react";
import { formatViewsAr, onThumbLoad, thumbRef, thumbUrl, type HomeVideo } from "@/data/home-videos";
import { useVideoPlayer } from "@/components/home/video-player";

/** Tappable YouTube thumbnail. Nothing from YouTube but the image loads before a tap. */
export function VideoCard({
  video,
  label,
  clamp = video.kind === "short" ? 3 : 2,
  className = "",
  titleSize = "text-[15px]",
}: {
  video: HomeVideo;
  label?: string;
  clamp?: 2 | 3;
  className?: string;
  titleSize?: string;
}) {
  const { play } = useVideoPlayer();
  const [fallback, setFallback] = useState(false);
  const w = video.kind === "long" ? 1280 : 405;
  const h = video.kind === "long" ? 720 : 720;
  return (
    <button
      type="button"
      onClick={() => play(video)}
      aria-label={`تشغيل الفيديو: ${video.title}`}
      className={
        "lift group relative block shrink-0 overflow-hidden rounded-ds-lg bg-ink text-start focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/60 " +
        className
      }
    >
      <img
        src={thumbUrl(video, fallback)}
        ref={thumbRef} onLoad={onThumbLoad}
        onError={() => !fallback && video.kind === "short" && setFallback(true)}
        alt=""
        loading="lazy"
        decoding="async"
        width={w}
        height={h}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-ds-base ease-ds group-hover:scale-[1.04] motion-reduce:transition-none"
      />
      <span
        aria-hidden
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to top, rgba(61,31,27,.9) 0%, rgba(61,31,27,.72) 50%, rgba(61,31,27,0) 78%), linear-gradient(rgba(${video.tint},.35), rgba(${video.tint},.35))`,
        }}
      />
      {label && (
        <span className="absolute start-3 top-3 rounded-ds-pill px-2.5 py-1 font-display text-[13px] font-bold text-cream-50" style={{ background: "rgba(17,15,13,.6)" }}>
          {label}
        </span>
      )}
      <span className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-3">
        <span className={"font-display font-extrabold leading-snug text-cream-50 " + titleSize + (clamp === 3 ? " line-clamp-3" : " line-clamp-2")}>
          {video.title}
        </span>
        <span className="flex items-center gap-2 whitespace-nowrap text-[13px] text-cream-50">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-500 text-night">
            <Play className="h-4 w-4 translate-x-[-1px] fill-current" strokeWidth={1.75} aria-hidden />
          </span>
          {formatViewsAr(video.views)}
        </span>
      </span>
    </button>
  );
}
